import type { Kennzahl, GetValuesFn } from './types'

// 16 visuell gut unterscheidbare Farben — zyklisch verwendet.
const ENTITY_COLORS = [
    '#004699',
    '#009EE3',
    '#E5007D',
    '#E53009',
    '#FFA500',
    '#228B22',
    '#8B4513',
    '#4B0082',
    '#00CED1',
    '#FFD700',
    '#FF1493',
    '#5F9EA0',
    '#D2691E',
    '#A52A2A',
    '#8A2BE2',
    '#FF6347'
]

/**
 * Baut pro Entität ein Dataset auf, wenn die Werte bereits aggregiert vorliegen.
 *
 * Format des `wert`-Felds: kommagetrennte `Name:Zahl`-Paare,
 * z. B. `"Algerien:2,Kenia:1,Ägypten:19"`.
 *
 * Im Unterschied zu `buildBundeslandChartData` werden die Zahlen direkt
 * übernommen (nicht durch Häufigkeitszählung ermittelt).
 */
export function buildAggregatedMapChartData(
    kennzahl: Kennzahl,
    getValues: GetValuesFn,
    baseYears: string[]
): any {
    const rows = getValues(kennzahl.sourceKennzahlId ?? kennzahl.kennzahlId)

    // Pro Semester: Map<Entitätsname, Wert>
    const valuesBySemester: Record<string, Record<string, number>> = {}
    const entitySet = new Set<string>()

    rows.forEach((row) => {
        const counts: Record<string, number> = {}
        const raw = typeof row.wert === 'string' ? row.wert : ''
        raw.split(',').forEach((token) => {
            const colonIdx = token.lastIndexOf(':')
            if (colonIdx === -1) return
            const name = token.slice(0, colonIdx).trim()
            const value = Number(token.slice(colonIdx + 1).trim())
            if (!name || isNaN(value)) return
            counts[name] = (counts[name] ?? 0) + value
            entitySet.add(name)
        })
        valuesBySemester[row.semester] = counts
    })

    if (entitySet.size === 0) return false

    // Alphabetisch sortiert (deutsche Locale) für stabile Farb- und Legenden-Reihenfolge
    const entities = Array.from(entitySet).sort((a, b) => a.localeCompare(b, 'de'))

    let maxValue = 0
    const datasets = entities.map((entity, idx) => {
        const dataPoints = baseYears.map((semester) => {
            const counts = valuesBySemester[semester]
            if (!counts || !(entity in counts)) {
                return { x: semester, y: null as number | null }
            }
            const y = counts[entity]
            if (y > maxValue) maxValue = y
            return { x: semester, y }
        })

        const color = ENTITY_COLORS[idx % ENTITY_COLORS.length]

        return {
            label: entity,
            kuerzel: entity,
            data: dataPoints,
            borderColor: color,
            backgroundColor: color,
            tension: 0,
            borderWidth: 2,
            pointRadius: 2,
            fill: false,
            spanGaps: false
        }
    })

    // maxValue auf das erste Dataset — LineChart liest die Skalierung von datasets[0].maxValue.
    const scaledMax = maxValue ? maxValue * 1.15 : 10
    ;(datasets[0] as Record<string, unknown>).maxValue = scaledMax

    return { labels: baseYears, datasets }
}
